import React, { createContext, useContext, useState } from 'react'
import { useTranslation, Translation } from 'react-i18next';
import { PageError } from 'da-frontend-shared-ui-components/lib/Errors/PageError';
import { PageErrorBar } from 'da-frontend-shared-ui-components/lib/Errors/PageErrorBar';

const InitialState = {
  message: "",
  pageError: ""
}

type ErrorState = typeof InitialState;

/** Error Context */
let ErrorContext = createContext<[ErrorState, (action: ErrorState) => void]>([InitialState, null]);

/** Display small error message */
const ErrorDisplay = React.memo(({ closeable=false, style }: { closeable?: boolean, style?: React.CSSProperties }) => {
  const { t }         = useTranslation();
  const [{ message }] = useContext(ErrorContext);

  if (message)
    return <PageErrorBar error={t(`errors.${message}`)} style={style} closeable={closeable} />
  else 
    return null;
});

/** Large page level error display */
const PageErrorDisplay = React.memo(({ children }: { children: React.ReactNode }) => {
  const { t }           = useTranslation();
  const [{ pageError }] = useContext(ErrorContext);

  if (pageError)
    return <PageError title={t`errors.title`} description={t(`errors.${pageError}`)} />
  else
    return children as JSX.Element;
});

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { error?: any }> {
  constructor(props) {
    super(props);
    this.state = { error: undefined };
  }
  
  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    // You can also log the error to an error reporting service
    // logErrorToMyService(error, errorInfo);
  }

  render() {
    if (this.state.error) {
      return <Translation>
        {t => <PageError title={t`errors.title`} description={t(`errors.${this.state.error}`)} />}
      </Translation>;
    }

    return this.props.children; 
  }
}

/** Error Context Provider */
const ErrorContextProvider = ({ children }: { children?: React.ReactNode }) => (
  <ErrorContext.Provider value={useState(InitialState)}>
    <PageErrorDisplay>
      {children}
    </PageErrorDisplay>
  </ErrorContext.Provider>
);

/** Dispatch error */
const useError = () => {
  const [ {message: errr}, dispatch ] = useContext(ErrorContext);

  const showError = (message: string) => {
    if (errr !== message)
      dispatch({ message, pageError: "" });
  }

  return showError;
}

/** Dispatch error */
const usePageError = () => {
  const [ {pageError: errr}, dispatch ] = useContext(ErrorContext);

  const showError = (pageError: string) => {
    if (errr !== pageError)
      dispatch({ pageError, message: "" });
  }

  return showError;
}

const catchError = <T extends object>(WrappedComponent: React.ComponentType<T>) => 
  (props: T) =>
  <ErrorContextProvider>
    <WrappedComponent {...props} />
  </ErrorContextProvider>

export default useError;
export {
  usePageError,
  ErrorContextProvider,
  ErrorDisplay,
  catchError,
  ErrorBoundary
};